// What is under the pointer: a ray through the scene, and the body in the
// `Hybrid` whose mesh it lands on.
//
// Shared by the two modes that work the canvas -- `nudge.ts`, which pushes what
// it picks, and `fix.ts`, which welds it -- so that both ask the `Hybrid` the
// same question and cannot drift apart on how a mesh is named.
//
// **The map from mesh to body is built at each pick rather than kept.** The
// alternative is a second record of the scene to hold in step with every drop,
// every "Edit config" and every object placed, and it would cost one pass
// either way.

import { InterfaceHybrid } from "simulator";
import { Simulator } from "gorilla-physics-ui";
import { Mesh, Ray, Raycaster, Vector2, Vector3 } from "three";

const raycaster = new Raycaster();

// A body in the scene, and where on it the ray landed.
export interface Picked {
  articulated: number;
  body: number;
  point: Vector3;
}

// The pointer as a ray through the scene.
export function aim(simulator: Simulator, event: PointerEvent): Ray {
  const canvas = simulator.graphics.renderer.domElement;
  const box = canvas.getBoundingClientRect();
  const ndc = new Vector2(
    ((event.clientX - box.left) / box.width) * 2 - 1,
    -((event.clientY - box.top) / box.height) * 2 + 1,
  );
  raycaster.setFromCamera(ndc, simulator.graphics.camera);
  return raycaster.ray;
}

// Every mesh `addArticulatedVisuals` made, and which body each belongs to. A
// name is `frame_articulated_body` and the visual's index, exactly as
// `index.ts` builds it.
function pickable(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
): Map<Mesh, { articulated: number; body: number }> {
  const found = new Map<Mesh, { articulated: number; body: number }>();
  for (let i = 0; i < hybrid.n_articulated(); i++) {
    for (let j = 0; j < hybrid.n_bodies_articulated(i); j++) {
      const frameName = hybrid.frame_articulated_body(i, j);
      for (let k = 0; k < hybrid.n_visuals_articulated_body(i, j); k++) {
        const mesh = simulator.meshes.get(`${frameName}-${k}`);
        if (mesh) {
          found.set(mesh, { articulated: i, body: j });
        }
      }
    }
  }
  return found;
}

// The body a pointer event points at, or nothing if it points at empty space.
export function pick(
  simulator: Simulator,
  hybrid: InterfaceHybrid,
  event: PointerEvent,
): Picked | undefined {
  aim(simulator, event);
  const owners = pickable(simulator, hybrid);
  const hits = raycaster.intersectObjects(Array.from(owners.keys()), false);
  const hit = hits[0];
  if (!hit) {
    return undefined;
  }
  const owner = owners.get(hit.object as Mesh);
  if (!owner) {
    return undefined;
  }
  return { ...owner, point: hit.point.clone() };
}
